import React, { useState, useEffect } from 'react';
import Apiconnect from "../services/Apiconnect.js";
import { Spinner, Table, Card, Button, Form, Pagination, Modal, Row, Col } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaChevronRight, FaChevronLeft } from 'react-icons/fa';

const styles = {
  addButton: {
    backgroundColor: '#D1D2D2',
    color: 'black',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    width: '95px',
    height: '35px',
  },
  searchInput: {
    width: '250px',
    marginRight: '10px',
  },
  searchButton: {
    marginRight: '10px',
  },
};

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const [showModal, setShowModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [packages, setPackages] = useState([]);
  const [customersWithPackageNames, setCustomersWithPackageNames] = useState([]);

  useEffect(() => {
    fetchPackages();
    fetchCustomers();
  }, []);

  // Update customers with package names whenever customers or packages change
  useEffect(() => {
    if (customers.length > 0 && packages.length > 0) {
      const enrichedCustomers = customers.map(customer => {
        const customerPackage = packages.find(pkg => pkg.id === Number(customer.package_id));
        return {
          ...customer,
          package_name: customerPackage ? customerPackage.name : 'N/A'
        };
      });
      setCustomersWithPackageNames(enrichedCustomers);
      setFilteredCustomers(enrichedCustomers);
    }
  }, [customers, packages]);

  const fetchPackages = async () => {
    try {
      const response = await Apiconnect.getData("packages");
      if (response.data && response.data.status === "success") {
        setPackages(response.data.data || []);
      } else {
        toast.error("Failed to fetch Packages");
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast.error('An error occurred while fetching packages');
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError("Authentication token not found. Please login again.");
        setIsLoading(false);
        return;
      }

      const response = await Apiconnect.postData("auth/customerlist", token);

      if (response.data && response.data.status === 1) {
        setCustomers(response.data.customers || []);
      } else {
        const errorMessage = response.data?.message || 'Failed to fetch customer list';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error('An error occurred while fetching customers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    const filtered = customersWithPackageNames.filter((customer) =>
      `${customer.fname || ''} ${customer.lname || ''}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCustomers(filtered);
  };

  const handleReset = () => {
    setSearchTerm('');
    setFilteredCustomers(customersWithPackageNames);
  };

  const handleEdit = (customer) => {
    setSelectedCustomer({ ...customer });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCustomer(null);
  };

  const handleChange = (field, value) => {
    setSelectedCustomer((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const formatForApi = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toISOString().split('T')[0];
  };

  const handleSubmit = async () => {
    if (!selectedCustomer) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error("Authentication token not found. Please login again.");
        return;
      }

      // Clean and validate data before sending
      const updatedCustomer = {
        fname: selectedCustomer.fname || '',
        lname: selectedCustomer.lname || '',
        phone: selectedCustomer.phone || '',
        email: selectedCustomer.email || '',
        address: selectedCustomer.address || '',
        zip: selectedCustomer.zip || '',
        package_id: selectedCustomer.package_id || '',
        expire_date: formatForApi(selectedCustomer.expire_date) || '',
      };

      try {
        const response = await Apiconnect.postDataWithToken(
          `auth/edit-customer/${selectedCustomer.id}`,
          token,
          updatedCustomer
        );

        if (response.data && response.data.status === 1) {
          toast.success("Customer updated successfully!");
          setShowModal(false);
          fetchCustomers(); // Refresh the list
        } else {
          toast.error(response.data?.message || "Failed to update customer.");
        }
      } catch (apiError) {
        console.error("API Error:", apiError);
        toast.error("API Error: " + (apiError.message || "Unknown error"));
      }
    } catch (error) {
      console.error("General Error:", error);
      toast.error("An unexpected error occurred.");
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handlePrevious = () => currentPage > 1 && setCurrentPage(currentPage - 1);
  const handleNext = () => currentPage < totalPages && setCurrentPage(currentPage + 1);

  return (
    <div className="container-fluid">
      <Card className="shadow">
        <Card.Header className="bg-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0" style={{ fontSize: '20px', color: 'white' }}>Vendor List</h4>
          </div>
        </Card.Header>

        <div className="d-flex mb-2 mt-3 ml-3">
          <Form.Control
            type="text"
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          <Button variant="primary" onClick={handleSearch} style={styles.searchButton}>Search</Button>
          <Button variant="secondary" onClick={handleReset}>Reset</Button>
        </div>

        <Card.Body>
          {isLoading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading customers...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : filteredCustomers.length === 0 ? (
            <div className="alert alert-info">No customers found.</div>
          ) : (
            <div className="table-responsive">
              <Table striped hover bordered className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Package</th>
                    <th>Expiration Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((customer, index) => (
                    <tr key={index}>
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>{`${customer.fname || ''} ${customer.lname || ''}`}</td>
                      <td>{customer.email || 'N/A'}</td>
                      <td>{customer.phone || 'N/A'}</td>
                      <td>{customer.package_name || 'N/A'}</td>
                      <td>{customer.expire_date ? new Date(customer.expire_date).toDateString() : 'N/A'}</td>
                      <td>
                        <Button
                          variant="dark"
                          size="sm"
                          style={{ padding: '1px 5px', fontSize: '10px', width: '50px', height: '25px' }}
                          onClick={() => handleEdit(customer)}
                        >
                          ✏️ Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>

        <Card.Footer>
          <Pagination className="justify-content-center">
            <Pagination.Prev onClick={handlePrevious} disabled={currentPage === 1}><FaChevronLeft /></Pagination.Prev>
            {[...Array(totalPages).keys()].map((page) => (
              <Pagination.Item
                key={page + 1}
                active={page + 1 === currentPage}
                onClick={() => handlePageChange(page + 1)}
              >
                {page + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next onClick={handleNext} disabled={currentPage === totalPages}><FaChevronRight /></Pagination.Next>
          </Pagination>
        </Card.Footer>
      </Card>

      {/* Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton style={{ backgroundColor: '#5867dd' }}>
          <Modal.Title style={{ color: 'white' }}>Edit Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCustomer && (
            <Form>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedCustomer.fname}
                    onChange={(e) => handleChange('fname', e.target.value)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedCustomer.lname}
                    onChange={(e) => handleChange('lname', e.target.value)}
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedCustomer.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={selectedCustomer.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedCustomer.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Zip</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedCustomer.zip}
                    onChange={(e) => handleChange('zip', e.target.value)}
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Package</Form.Label>
                  <Form.Select
                    value={selectedCustomer.package_id || ''}
                    onChange={(e) => handleChange('package_id', e.target.value)}
                  >
                    <option value="">Select Package</option>
                    {packages.map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>
                        {pkg.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={6}>
                  <Form.Label>Expire Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={selectedCustomer.expire_date ? new Date(selectedCustomer.expire_date).toISOString().split('T')[0] : ''}
                    onChange={(e) => handleChange('expire_date', e.target.value)}
                  />
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
          <Button variant="primary" onClick={handleSubmit}>Save Changes</Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />
    </div>
  );
};

export default CustomerList;